import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import styled from 'styled-components';
import { IoEllipsisVertical } from 'react-icons/io5';
import { IoWarningOutline } from 'react-icons/io5';
import { useInvoiceTotal } from '../../../../hooks/useInvoiceTotal';

interface InvoiceDetailCardProps {
  data: {
    total: number;
    synced: number;
    notSynced: number;
    syncFailed: number;
    hasErrors: number;
    archived: number;
    statusBreakdown?: Array<{
      status_label: string;
      status_color: string;
      count: number;
    }>;
  };
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

const InvoiceDetailCard: React.FC<InvoiceDetailCardProps> = ({ data, dateRange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [paymentData, setPaymentData] = useState<Record<number, any[]>>({});
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [debugData, setDebugData] = useState<any>(null);
  
  // Cursor-based pagination state
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [invoiceList, setInvoiceList] = useState<any[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    hasMore: false,
    hasPrev: false,
    totalCount: 0,
    firstCursor: null as string | null,
    lastCursor: null as string | null,
  });

  // InvoiceStatusBadge component for displaying invoice status
  const InvoiceStatusBadge: React.FC<{ status: string | null }> = ({ status }) => {
    if (!status) {
      return (
        <StatusBadge type="unknown">
          No Status
        </StatusBadge>
      );
    }

    // Shorten long status names to fit in compact badges
    const shortenStatus = (statusText: string): string => {
      const statusLower = statusText.toLowerCase();
      if (statusLower.includes('partially paid')) return 'PARTIAL';
      if (statusLower.includes('fully paid')) return 'PAID';
      if (statusLower.includes('overdue')) return 'OVERDUE';
      if (statusLower.includes('voided') || statusLower.includes('cancelled')) return 'VOID';
      if (statusLower.includes('draft')) return 'DRAFT';
      if (statusLower.includes('sent')) return 'SENT';
      if (statusLower.includes('pending')) return 'PENDING';
      return statusText;
    };

    const displayStatus = shortenStatus(status);

    // Try to find the status in the breakdown data to get the actual color
    const statusBreakdownItem = data.statusBreakdown?.find(
      item => item.status_label.toLowerCase() === status.toLowerCase()
    );

    if (statusBreakdownItem) {
      // Convert RGB color to hex and use the actual color from the breakdown data
      const backgroundColor = rgbToHex(statusBreakdownItem.status_color);
      return (
        <StatusBadgeCustom 
          style={{ 
            backgroundColor: backgroundColor,
            color: getContrastColor(backgroundColor)
          }}
          title={status} // Show full status on hover
        >
          {displayStatus}
        </StatusBadgeCustom>
      );
    }

    // Fallback to the original logic if no matching status found
    const statusLower = status.toLowerCase();
    let type: string = 'default';

    if (statusLower.includes('void') || statusLower.includes('cancelled')) {
      type = 'voided';
    } else if (statusLower.includes('paid') || statusLower.includes('complete')) {
      type = 'paid';
    } else if (statusLower.includes('sent') || statusLower.includes('sent')) {
      type = 'sent';
    } else if (statusLower.includes('draft') || statusLower.includes('pending')) {
      type = 'draft';
    } else if (statusLower.includes('overdue') || statusLower.includes('past due')) {
      type = 'overdue';
    }

    return (
      <StatusBadge type={type} title={status}>
        {displayStatus}
      </StatusBadge>
    );
  };

  // Helper function to convert RGB string to semi-transparent hex color
  const rgbToHex = (rgbString: string): string => {
    // Handle RGB format like "30,132,73" or "30, 132, 73"
    const rgb = rgbString.split(',').map(val => parseInt(val.trim()));
    if (rgb.length !== 3 || rgb.some(val => isNaN(val))) {
      return '#000000'; // fallback to black if invalid
    }
    
    const [r, g, b] = rgb;
    // Add 40% opacity (0.4 alpha) to make colors semi-transparent
    return `rgba(${r}, ${g}, ${b}, 0.4)`;
  };

  // Helper function to determine text color based on background color
  const getContrastColor = (backgroundColor: string): string => {
    // Simple contrast calculation - you might want to use a more sophisticated library
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  };

  // CanonicalTotalCell component for displaying invoice totals
  const CanonicalTotalCell: React.FC<{ invoiceId: number }> = ({ invoiceId }) => {
    const { data: totalData, isLoading, isError } = useInvoiceTotal(invoiceId);

    if (isLoading) {
      return (
        <TotalCell>
          <LoadingText>Calculating...</LoadingText>
        </TotalCell>
      );
    }

    if (isError) {
      return (
        <TotalCell>
          <ErrorContainer title="Failed to calculate canonical total">
            <IoWarningOutline size={16} color="#dc2626" />
            <ErrorText>Error</ErrorText>
          </ErrorContainer>
        </TotalCell>
      );
    }

    if (!totalData) {
      return (
        <TotalCell>
          <NoDataText>No data</NoDataText>
        </TotalCell>
      );
    }

    return (
      <TotalCell>
        <TotalAmount>
          ${Number(totalData.grandTotal).toFixed(2)}
        </TotalAmount>
      </TotalCell>
    );
  };

  // PaymentsCell component for displaying payment information
  const PaymentsCell: React.FC<{ invoiceId: number }> = ({ invoiceId }) => {
    const { data: totalData } = useInvoiceTotal(invoiceId);
    const [payments, setPayments] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
      const fetchPayments = async () => {
        setIsLoading(true);
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/invoice-payments`,
            {
              params: { invoiceId }
            }
          );
          setPayments(response.data.data || []);
        } catch (error) {
          console.error('Error fetching payments:', error);
          setPayments([]);
        } finally {
          setIsLoading(false);
        }
      };

      if (invoiceId) {
        fetchPayments();
      }
    }, [invoiceId]);

    if (isLoading) {
      return (
        <PaymentsCellContainer>
          <LoadingText>Loading...</LoadingText>
        </PaymentsCellContainer>
      );
    }

    const totalPayments = payments.reduce((sum, payment) => sum + ((payment.amount || 0) / 100), 0);
    const invoiceTotal = totalData ? Number(totalData.grandTotal) : 0;
    const balance = invoiceTotal - totalPayments;
    const paymentCount = payments.length;

    return (
      <PaymentsCellContainer>
        <PaymentRow>
          <PaymentLabel>Total:</PaymentLabel>
          <PaymentAmount>${invoiceTotal.toFixed(2)}</PaymentAmount>
        </PaymentRow>
        <PaymentRow>
          <PaymentLabel>Paid:</PaymentLabel>
          <PaymentAmount>${totalPayments.toFixed(2)} <PaymentCountBadge>({paymentCount})</PaymentCountBadge></PaymentAmount>
        </PaymentRow>
        <PaymentRow>
          <PaymentLabel>Balance:</PaymentLabel>
          <BalanceAmount balance={balance}>
            ${balance.toFixed(2)}
          </BalanceAmount>
        </PaymentRow>
      </PaymentsCellContainer>
    );
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  // Fetch invoices using cursor-based pagination
  const fetchInvoices = useCallback(async (direction: 'next' | 'prev' = 'next', newCursor: string | null = null, category: string | null = null) => {
    setLoadingInvoices(true);
    try {
      const currentCursor = newCursor !== null ? newCursor : cursor;
      const categoryToUse = category !== null ? category : selectedCategory;
      const params: any = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        limit: itemsPerPage,
        direction: direction
      };
      if (categoryToUse) {
        params.category = categoryToUse;
      }
      if (currentCursor) {
        params.cursor = currentCursor;
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/local-summary/invoice-ids-paginated`,
        { params }
      );
      
      setInvoiceList(response.data.data.invoices);
      setPaginationInfo({
        hasMore: response.data.data.hasMore,
        hasPrev: response.data.data.hasPrev,
        totalCount: response.data.data.totalCount,
        firstCursor: response.data.data.firstCursor,
        lastCursor: response.data.data.lastCursor,
      });
      
      // Update cursor after navigation
      // Note: "next" = decrement (lower IDs), "prev" = increment (higher IDs)
      if (response.data.data.invoices.length > 0) {
        if (direction === 'next') {
          // Moving to "next" (decrement): store current firstCursor in history for going back
          if (response.data.data.firstCursor && currentCursor !== null) {
            setCursorHistory(prev => [...prev, response.data.data.firstCursor]);
          }
          // Update cursor to lastCursor (lowest ID on page) for next decrement navigation
          setCursor(response.data.data.lastCursor);
        } else {
          // Moving to "prev" (increment): the API returned records with higher IDs
          const newHistory = [...cursorHistory];
          if (newHistory.length > 0) {
            newHistory.pop();
            setCursorHistory(newHistory);
          }
          if (newHistory.length === 0) {
            setCursor(null); // We're at the top (highest IDs)
          } else {
            setCursor(response.data.data.lastCursor || null);
          }
        }
      }
      
      setLoadingInvoices(false);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setLoadingInvoices(false);
    }
  }, [cursor, cursorHistory, itemsPerPage, selectedCategory, dateRange]);

  // Load invoices when expanded or category changes
  useEffect(() => {
    if (isExpanded) {
      // Reset cursor when expanding or changing category
      setCursor(null);
      setCursorHistory([]);
      fetchInvoices('next', null, selectedCategory);
    }
  }, [isExpanded, selectedCategory, dateRange, itemsPerPage, fetchInvoices]);

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      setSelectedCategory(null);
      setCursor(null);
      setCursorHistory([]);
      setInvoiceList([]);
    }
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setCursor(null);
    setCursorHistory([]);
  };

  // Navigate to next page (decrement - lower IDs)
  const paginateNext = () => {
    if (paginationInfo.hasMore) {
      // Use current cursor (lastCursor = lowest ID on page) or null for first page
      fetchInvoices('next', cursor);
    }
  };

  // Navigate to previous page (increment - higher IDs)
  const paginatePrev = () => {
    if (paginationInfo.hasPrev) {
      // For increment navigation, use the lastCursor of the current page
      // This queries records with IDs > lastCursor (higher IDs)
      const prevCursor = paginationInfo.lastCursor;
      if (prevCursor) {
        fetchInvoices('prev', prevCursor);
      } else {
        // No lastCursor means we're at top, reset
        setCursor(null);
        setCursorHistory([]);
        fetchInvoices('next', null);
      }
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setItemsPerPage(newPageSize);
    setCursor(null);
    setCursorHistory([]);
  };

  const handleCopyAllIds = () => {
    // Copy IDs from current page only (no cached list)
    const currentIds = invoiceList.map((invoice: any) => invoice.id).join(', ');
    navigator.clipboard.writeText(currentIds);
  };

  const handleOpenStatusModal = (invoice: any) => {
    setSelectedInvoice(invoice);
    setStatusModalOpen(true);
  };

  const handleCloseStatusModal = () => {
    setStatusModalOpen(false);
    setSelectedInvoice(null);
  };

  const handleCloseDebugModal = () => {
    setDebugModalOpen(false);
    setDebugData(null);
  };

  const copyDebugData = () => {
    if (debugData) {
      navigator.clipboard.writeText(JSON.stringify(debugData, null, 2));
    }
  };

  const handleResyncInvoice = () => {
    // TODO: Implement resync functionality
    
  };

  const handleForceSyncInvoice = () => {
    // TODO: Implement force sync functionality
    
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const actionMenus = document.querySelectorAll('[data-action-menu]');
      let clickedInsideMenu = false;
      
      actionMenus.forEach(menu => {
        if (menu.contains(target)) {
          clickedInsideMenu = true;
        }
      });
      
      if (!clickedInsideMenu) {
        setOpenActionMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleActionMenuToggle = (invoiceId: number) => {
    
    setOpenActionMenu(openActionMenu === invoiceId ? null : invoiceId);
  };

  const handleActionClick = (action: string, invoice: any) => {
    
    setOpenActionMenu(null);
    
    switch (action) {
      case 'copy-id':
        copyToClipboard(invoice.id.toString());
        
        break;
      case 'status':
        handleOpenStatusModal(invoice);
        
        break;
      case 'open-tab':
        openInvoiceInNewTab(invoice.id);
        
        break;
      case 'debug':
        setDebugData({
          invoiceId: invoice.id,
          invoice: invoice,
          message: `Debug data for invoice ${invoice.id}`
        });
        setDebugModalOpen(true);
        console.warn('Opening debug modal for invoice:', invoice.id);
        break;
      case 'resync':
        console.warn('🔄 Re-sync Invoice requested for invoice:', invoice.id);
        // TODO: Implement actual re-sync logic
        // For now, just show a mock toast/alert
        alert(`🔄 Re-sync Invoice #${invoice.id} - This is a mock implementation`);
        break;
      default:
        
        break;
    }
  };

  const openInvoiceInNewTab = (invoiceId: number) => {
    const url = `${window.location.origin}/invoices/${invoiceId}`;
    window.open(url, '_blank');
  };

  const fetchPaymentData = async (invoiceId: number) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/invoice-payments`,
        {
          params: {
            invoiceId: invoiceId
          }
        }
      );
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching payment data:', error);
      return [];
    }
  };

  const handleRowExpand = async (invoiceId: number) => {
    const newExpandedRows = new Set(expandedRows);
    
    if (newExpandedRows.has(invoiceId)) {
      // Collapse row
      newExpandedRows.delete(invoiceId);
      setExpandedRows(newExpandedRows);
    } else {
      // Expand row
      newExpandedRows.add(invoiceId);
      setExpandedRows(newExpandedRows);
      
      // Fetch payment data if not already loaded
      if (!paymentData[invoiceId]) {
        const payments = await fetchPaymentData(invoiceId);
        setPaymentData(prev => ({
          ...prev,
          [invoiceId]: payments
        }));
      }
    }
  };

  // Calculate approximate page number for display
  const approximatePageNumber = cursorHistory.length + 1;
  const currentPage = approximatePageNumber || 1;
  const totalPages = Math.max(1, Math.ceil(paginationInfo.totalCount / itemsPerPage));
  

  const renderSummary = () => (
    <>
      <CardHeader onClick={handleToggleExpand}>
        <h3>Invoices</h3>
        <ExpandIcon isExpanded={isExpanded}>▼</ExpandIcon>
      </CardHeader>

      <SummaryGrid>
        <SummaryItem>
          <SummaryValue>{formatNumber(data.total)}</SummaryValue>
          <SummaryLabel>Total Invoices</SummaryLabel>
        </SummaryItem>

        <SummaryItem>
          <SummaryValue>{formatNumber(data.synced)}</SummaryValue>
          <SummaryLabel>Synced to QBO</SummaryLabel>
          <SummarySubtext>Based on qbol_id</SummarySubtext>
        </SummaryItem>

        <SummaryItem>
          <SummaryValue>{formatNumber(data.archived)}</SummaryValue>
          <SummaryLabel>Archived</SummaryLabel>
        </SummaryItem>
      </SummaryGrid>
    </>
  );

  const renderDetails = () => (
    <CardDetails>
      <CategoryTiles>
        <CategoryTile 
          onClick={() => handleCategorySelect('synced')}
          isActive={selectedCategory === 'synced'}
        >
          <CategoryTitle>Synced to QBO</CategoryTitle>
          <CategoryCount>{formatNumber(data.synced)}</CategoryCount>
          <CategoryPercent>{data.total > 0 ? `${Math.round((data.synced / data.total) * 100)}%` : '0%'}</CategoryPercent>
        </CategoryTile>

        <CategoryTile 
          onClick={() => handleCategorySelect('not-synced')}
          isActive={selectedCategory === 'not-synced'}
        >
          <CategoryTitle>Not Synced (&gt;$0 and Active)</CategoryTitle>
          <CategoryCount>{formatNumber(data.notSynced)}</CategoryCount>
          <CategoryPercent>{data.total > 0 ? `${Math.round((data.notSynced / data.total) * 100)}%` : '0%'}</CategoryPercent>
        </CategoryTile>

        <CategoryTile 
          onClick={() => handleCategorySelect('archived')}
          isActive={selectedCategory === 'archived'}
        >
          <CategoryTitle>Archived</CategoryTitle>
          <CategoryCount>{formatNumber(data.archived)}</CategoryCount>
          <CategoryPercent>{data.total > 0 ? `${Math.round((data.archived / data.total) * 100)}%` : '0%'}</CategoryPercent>
        </CategoryTile>

        <CategoryTile 
          onClick={() => handleCategorySelect('error')}
          isActive={selectedCategory === 'error'}
        >
          <CategoryTitle>Has Errors</CategoryTitle>
          <CategoryCount>{formatNumber(data.hasErrors)}</CategoryCount>
          <CategoryPercent>{data.total > 0 ? `${Math.round((data.hasErrors / data.total) * 100)}%` : '0%'}</CategoryPercent>
        </CategoryTile>

        <CategoryTile 
          onClick={() => handleCategorySelect('failed')}
          isActive={selectedCategory === 'failed'}
        >
          <CategoryTitle>All Invoices</CategoryTitle>
          <CategoryCount>{formatNumber(data.total)}</CategoryCount>
          <CategoryPercent>100%</CategoryPercent>
        </CategoryTile>
      </CategoryTiles>

      {selectedCategory && (
        <InvoiceTableSection>
          <TableHeader>
            <TableTitle>
              {selectedCategory === 'synced' ? 'Synced Invoices' : 
               selectedCategory === 'not-synced' ? 'Not Synced Invoices (&gt;$0 and Active)' :
               selectedCategory === 'archived' ? 'Archived Invoices' :
               selectedCategory === 'failed' ? 'All Invoices' :
               'Invoices with Errors'}
            </TableTitle>
                         <TableControls>
               <TableSubtitle>
                 Showing {invoiceList.length} of {paginationInfo.totalCount} invoices
               </TableSubtitle>
               <TableActions>
                 <CopyAllButton onClick={handleCopyAllIds}>
                   Copy All IDs
                 </CopyAllButton>
                 <PageSizeSelector>
                   <PageSizeLabel>Show:</PageSizeLabel>
                   <PageSizeSelect 
                     value={itemsPerPage} 
                     onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                   >
                     <option value={5}>5 per page</option>
                     <option value={10}>10 per page</option>
                     <option value={25}>25 per page</option>
                     <option value={50}>50 per page</option>
                     <option value={100}>100 per page</option>
                   </PageSizeSelect>
                 </PageSizeSelector>
               </TableActions>
             </TableControls>
          </TableHeader>

          <InvoiceTable>
                         <thead>
               <tr>
                 <th>ID</th>
                 <th>Date</th>
                 <th>QBO ID</th>
                 <th>Sync</th>
                 <th>Status</th>
                 <th>Total</th>
                 <th>Payments & Balance</th>
                 <th>Actions</th>
               </tr>
             </thead>
            <tbody>
              {loadingInvoices ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
                    Loading invoices...
                  </td>
                </tr>
              ) : invoiceList.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
                    No invoices found
                  </td>
                </tr>
              ) : (
                invoiceList.map((invoice: any, index: number) => (
                <React.Fragment key={index}>
                  <tr 
                    onClick={() => handleRowExpand(invoice.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <InvoiceIdCell>
                        {expandedRows.has(invoice.id) ? '▼' : '▶'} #{invoice.id}
                      </InvoiceIdCell>
                    </td>
                    <td>
                      <DateCell>{new Date(invoice.creationDate).toLocaleDateString()}</DateCell>
                    </td>
                    <td>
                      {invoice.qboId ? (
                        <QboIdCell>QBO: {invoice.qboId}</QboIdCell>
                      ) : (
                        <MissingQboCell>No QBO ID</MissingQboCell>
                      )}
                    </td>
                                         <td>
                       <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                         {/* Sync Status */}
                         {selectedCategory === 'archived' && (
                           <StatusCell type="archived">Archived</StatusCell>
                         )}
                         {selectedCategory === 'failed' && (
                           <StatusCell type="failed">Sync Failed</StatusCell>
                         )}
                         {selectedCategory === 'error' && (
                           <StatusCell type="error">Has Errors</StatusCell>
                         )}
                         {selectedCategory === 'synced' && (
                           <StatusCell type="synced">Synced</StatusCell>
                         )}
                         {selectedCategory === 'not-synced' && (
                           <StatusCell type="not-synced">Not Synced</StatusCell>
                         )}
                         
                         {/* Archived Status - shows regardless of category */}
                         {invoice.archiveDate && (
                           <ArchivedBadge title={`Archived on ${new Date(invoice.archiveDate).toLocaleDateString()}`}>
                             📁 Archived
                           </ArchivedBadge>
                         )}
                       </div>
                     </td>
                     <td>
                       <InvoiceStatusBadge status={invoice.statusLabel} />
                     </td>
                     <td>
                       <CanonicalTotalCell invoiceId={invoice.id} />
                     </td>
                     <td>
                       <PaymentsCell invoiceId={invoice.id} />
                     </td>
                    <td>
                      <ActionMenuContainer data-action-menu>
                        <ActionMenuButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleActionMenuToggle(invoice.id);
                          }}
                          title="Actions"
                        >
                          <IoEllipsisVertical size={16} />
                        </ActionMenuButton>
                        
                        {openActionMenu === invoice.id && (
                          <ActionMenuDropdown>
                            <ActionMenuItem onClick={() => handleActionClick('copy-id', invoice)}>
                              Copy ID
                            </ActionMenuItem>
                            <ActionMenuItem onClick={() => handleActionClick('status', invoice)}>
                              Status
                            </ActionMenuItem>
                            <ActionMenuItem onClick={() => handleActionClick('open-tab', invoice)}>
                              Open in New Tab
                            </ActionMenuItem>
                            <ActionMenuItem onClick={() => handleActionClick('debug', invoice)}>
                              Debug Data
                            </ActionMenuItem>
                            <ActionMenuItem onClick={() => handleActionClick('resync', invoice)}>
                              🔄 Re-sync Invoice
                            </ActionMenuItem>
                          </ActionMenuDropdown>
                        )}
                      </ActionMenuContainer>
                    </td>
                  </tr>
                  
                                     {/* Expanded Payment Information Row */}
                   {expandedRows.has(invoice.id) && (
                     <tr>
                       <td colSpan={8}>
                        <PaymentExpansionPanel>
                          <PaymentExpansionHeader>
                            <PaymentExpansionTitle>Payment Information</PaymentExpansionTitle>
                            {paymentData[invoice.id] ? (
                              <PaymentCount>
                                {paymentData[invoice.id].length} payment{paymentData[invoice.id].length !== 1 ? 's' : ''}
                              </PaymentCount>
                            ) : (
                              <PaymentCount>Loading...</PaymentCount>
                            )}
                          </PaymentExpansionHeader>
                          
                          {paymentData[invoice.id] && (
                            paymentData[invoice.id].length > 0 ? (
                              <PaymentTable>
                                <thead>
                                  <tr>
                                    <th>Payment ID</th>
                                    <th>Amount</th>
                                    <th>Date</th>
                                    <th>QBO ID</th>
                                    <th>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {paymentData[invoice.id].map((payment: any, paymentIndex: number) => (
                                    <tr key={paymentIndex}>
                                      <td>
                                        <PaymentIdCell>#{payment.id}</PaymentIdCell>
                                      </td>
                                      <td>
                                        <PaymentAmountCell>
                                          ${(payment.amount / 100).toFixed(2)}
                                        </PaymentAmountCell>
                                      </td>
                                      <td>
                                        <PaymentDateCell>
                                          {new Date(payment.receivedOn || payment.createdAt).toLocaleDateString()}
                                        </PaymentDateCell>
                                      </td>
                                      <td>
                                        {payment.qboId ? (
                                          <QboIdCell>QBO: {payment.qboId}</QboIdCell>
                                        ) : (
                                          <MissingQboCell>No QBO ID</MissingQboCell>
                                        )}
                                      </td>
                                      <td>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                          <PaymentStatusCell type={payment.isVoided ? 'voided' : payment.isRefund ? 'refund' : 'active'}>
                                            {payment.isVoided ? 'Voided' : payment.isRefund ? 'Refund' : 'Active'}
                                          </PaymentStatusCell>
                                          
                                          {/* Archived Status for payments */}
                                          {payment.archiveDate && (
                                            <ArchivedBadge title={`Archived on ${new Date(payment.archiveDate).toLocaleDateString()}`}>
                                              📁 Archived
                                            </ArchivedBadge>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </PaymentTable>
                            ) : (
                              <NoPaymentsMessage>
                                No payments found for this invoice.
                              </NoPaymentsMessage>
                            )
                          )}
                        </PaymentExpansionPanel>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                ))
              )}
            </tbody>
          </InvoiceTable>

          {totalPages > 1 && (
            <Pagination>
              <PaginationButton 
                onClick={paginatePrev}
                disabled={!paginationInfo.hasPrev}
              >
                ← Previous
              </PaginationButton>
              <PaginationInfo>
                Page {currentPage} of {totalPages}
              </PaginationInfo>
              <PaginationButton 
                onClick={paginateNext}
                disabled={!paginationInfo.hasMore}
              >
                Next →
              </PaginationButton>
            </Pagination>
          )}
          
          {/* Add bottom spacing to prevent menu cutoff */}
          <div style={{ height: '60px' }} />
        </InvoiceTableSection>
      )}
    </CardDetails>
  );

  return (
    <Card>
      {renderSummary()}
      {isExpanded && renderDetails()}
      
      {/* Status Modal */}
      {statusModalOpen && selectedInvoice && (
        <StatusModalOverlay onClick={handleCloseStatusModal}>
          <StatusModalContent onClick={(e) => e.stopPropagation()}>
            <StatusModalHeader>
              <StatusModalTitle>Invoice #{selectedInvoice.id} Details</StatusModalTitle>
              <CloseButton onClick={handleCloseStatusModal}>×</CloseButton>
            </StatusModalHeader>
            
            <StatusModalBody>
              <DetailSection>
                <DetailTitle>Basic Information</DetailTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailLabel>Invoice ID:</DetailLabel>
                    <DetailValue>#{selectedInvoice.id}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Invoice Number:</DetailLabel>
                    <DetailValue>{selectedInvoice.invoiceNumber || 'N/A'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Created Date:</DetailLabel>
                    <DetailValue>{new Date(selectedInvoice.creationDate).toLocaleDateString()}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Due Date:</DetailLabel>
                    <DetailValue>{selectedInvoice.dueDate ? new Date(selectedInvoice.dueDate).toLocaleDateString() : 'N/A'}</DetailValue>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <DetailTitle>QuickBooks Online</DetailTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailLabel>QBO ID:</DetailLabel>
                    <DetailValue>{selectedInvoice.qboId || 'Not synced'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Sync Date:</DetailLabel>
                    <DetailValue>{selectedInvoice.qboSyncDate ? new Date(selectedInvoice.qboSyncDate).toLocaleDateString() : 'N/A'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>QBO Status:</DetailLabel>
                    <DetailValue>{selectedInvoice.qboStatus || 'N/A'}</DetailValue>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <DetailTitle>Financial Information</DetailTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailLabel>Subtotal:</DetailLabel>
                    <DetailValue>${selectedInvoice.subtotal ? selectedInvoice.subtotal.toFixed(2) : '0.00'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Tax Amount:</DetailLabel>
                    <DetailValue>${selectedInvoice.taxAmount ? selectedInvoice.taxAmount.toFixed(2) : '0.00'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Total Amount:</DetailLabel>
                    <DetailValue>${selectedInvoice.totalAmount ? selectedInvoice.totalAmount.toFixed(2) : '0.00'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Paid Amount:</DetailLabel>
                    <DetailValue>${selectedInvoice.paidAmount ? selectedInvoice.paidAmount.toFixed(2) : '0.00'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Balance Due:</DetailLabel>
                    <DetailValue>${selectedInvoice.balanceDue ? selectedInvoice.balanceDue.toFixed(2) : '0.00'}</DetailValue>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <DetailTitle>Payments Applied</DetailTitle>
                <PaymentList>
                  <PaymentItem>
                    <PaymentDate>2025-01-15</PaymentDate>
                    <PaymentAmountLarge>$500.00</PaymentAmountLarge>
                    <PaymentMethod>Credit Card</PaymentMethod>
                  </PaymentItem>
                  <PaymentItem>
                    <PaymentDate>2025-01-20</PaymentDate>
                    <PaymentAmountLarge>$250.00</PaymentAmountLarge>
                    <PaymentMethod>Bank Transfer</PaymentMethod>
                  </PaymentItem>
                </PaymentList>
              </DetailSection>
            </StatusModalBody>

            <StatusModalFooter>
              <ModalButton onClick={handleCloseStatusModal}>
                Close
              </ModalButton>
              <ModalButton primary onClick={handleResyncInvoice}>
                Resync to QBO
              </ModalButton>
              <ModalButton primary onClick={handleForceSyncInvoice}>
                Force Sync
              </ModalButton>
                    </StatusModalFooter>
      </StatusModalContent>
    </StatusModalOverlay>
  )}

  {/* Debug Modal */}
  {debugModalOpen && debugData && (
    <DebugModalOverlay onClick={handleCloseDebugModal}>
      <DebugModalContent onClick={(e) => e.stopPropagation()}>
        <DebugModalHeader>
          <DebugModalTitle>Debug Data</DebugModalTitle>
          <CloseButton onClick={handleCloseDebugModal}>×</CloseButton>
        </DebugModalHeader>
        <DebugModalBody>
          <DebugDataContainer>
            <pre>{JSON.stringify(debugData, null, 2)}</pre>
          </DebugDataContainer>
        </DebugModalBody>
        <DebugModalFooter>
          <ModalButton onClick={copyDebugData}>
            Copy to Clipboard
          </ModalButton>
          <ModalButton onClick={handleCloseDebugModal}>
            Close
          </ModalButton>
        </DebugModalFooter>
      </DebugModalContent>
    </DebugModalOverlay>
  )}
    </Card>
  );
};

// Styled Components
const Card = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
  }
  
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
  }
`;

const ExpandIcon = styled.span<{ isExpanded: boolean }>`
  font-size: 14px;
  color: #6b7280;
  transform: ${props => props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.2s ease;
`;

const SummaryGrid = styled.div`
  display: flex;
  gap: 20px;
  padding: 20px;
`;

const SummaryItem = styled.div`
  text-align: center;
  flex: 1;
`;

const SummaryValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
`;

const SummaryLabel = styled.div`
  font-size: 13px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

const SummarySubtext = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
`;

const CardDetails = styled.div`
  border-top: 1px solid #e5e7eb;
`;

const CategoryTiles = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const CategoryTile = styled.div<{ isActive: boolean }>`
  background: ${props => props.isActive ? '#f97316' : 'white'};
  color: ${props => props.isActive ? 'white' : '#374151'};
  padding: 12px 16px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid ${props => props.isActive ? '#f97316' : '#d1d5db'};
  min-width: 120px;
  text-align: center;
  transition: all 0.2s ease;
  box-shadow: ${props => props.isActive ? '0 1px 3px 0 rgba(249, 115, 22, 0.3)' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)'};
  
  &:hover {
    background: ${props => props.isActive ? '#ea580c' : '#f9fafb'};
    border-color: ${props => props.isActive ? '#ea580c' : '#9ca3af'};
    transform: translateY(-1px);
  }
`;

const CategoryTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const CategoryCount = styled.div`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 4px;
`;

const CategoryPercent = styled.div`
  font-size: 11px;
  opacity: 0.9;
`;

const InvoiceTableSection = styled.div`
  padding: 20px;
`;

const TableHeader = styled.div`
  margin-bottom: 16px;
`;

const TableTitle = styled.h4`
  margin: 0 0 6px 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const TableSubtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
`;

const TableControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;

const TableActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CopyAllButton = styled.button`
  background: #f97316;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #ea580c;
    transform: translateY(-1px);
  }
`;

const PageSizeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PageSizeLabel = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`;

const PageSizeSelect = styled.select`
  font-size: 13px;
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #f97316;
  }
  
  &:focus {
    outline: none;
    border-color: #f97316;
    box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  }
`;

const InvoiceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  
  th {
    background: #f9fafb;
    padding: 8px 12px;
    text-align: left;
    font-weight: 600;
    color: #111827;
    border-bottom: 1px solid #e5e7eb;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  td {
    padding: 8px 12px;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;
  }
  
  tr:hover {
    background: #f9fafb;
  }
  
  tr:last-child td {
    border-bottom: none;
  }
`;

const InvoiceIdCell = styled.span`
  font-family: monospace;
  font-weight: 600;
  color: #f97316;
  font-size: 11px;
`;

const DateCell = styled.span`
  color: #374151;
  font-size: 11px;
`;

const QboIdCell = styled.span`
  font-size: 10px;
  color: #059669;
  background: #d1fae5;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 500;
`;

const MissingQboCell = styled.span`
  font-size: 10px;
  color: #dc2626;
  background: #fee2e2;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 500;
`;

const StatusCell = styled.span<{ type: string }>`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 500;
  
  ${props => {
    switch (props.type) {
      case 'synced':
        return 'color: #059669; background: #d1fae5;';
      case 'not-synced':
        return 'color: #dc2626; background: #fee2e2;';
      case 'archived':
        return 'color: #7c3aed; background: #ede9fe;';
      case 'failed':
        return 'color: #dc2626; background: #fee2e2;';
      case 'error':
        return 'color: #ea580c; background: #fed7aa;';
      default:
        return 'color: #6b7280; background: #f3f4f6;';
    }
  }}
`;

const ArchivedBadge = styled.span`
  font-size: 9px;
  padding: 1px 4px;
  border-radius: 2px;
  font-weight: 500;
  color: #7c3aed;
  background: #ede9fe;
  border: 1px solid #c4b5fd;
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;



const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

const PaginationButton = styled.button`
  background: white;
  border: 1px solid #d1d5db;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #f97316;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`;

// Status Modal Styled Components
const StatusModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const StatusModalContent = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
`;

const StatusModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const StatusModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  color: #6b7280;
  font-size: 24px;
  line-height: 1;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f3f4f6;
    color: #374151;
  }
`;

const StatusModalBody = styled.div`
  padding: 28px;
  overflow-y: auto;
  flex: 1;
`;

const DetailSection = styled.div`
  margin-bottom: 28px;
`;

const DetailTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`;

const DetailLabel = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 13px;
  color: #111827;
  font-weight: 600;
`;

const PaymentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PaymentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f9fafb;
  border-radius: 6px;
  border-left: 4px solid #f97316;
  border: 1px solid #e5e7eb;
`;

const PaymentDate = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`;

const PaymentAmountLarge = styled.span`
  font-size: 15px;
  color: #059669;
  font-weight: 600;
`;

const PaymentMethod = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`;

const StatusModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 24px 28px;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
`;

const ModalButton = styled.button<{ primary?: boolean }>`
  background: ${props => props.primary ? '#f97316' : 'white'};
  color: ${props => props.primary ? 'white' : '#374151'};
  border: 1px solid ${props => props.primary ? '#f97316' : '#d1d5db'};
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.primary ? '#ea580c' : '#f9fafb'};
    border-color: ${props => props.primary ? '#ea580c' : '#f97316'};
    transform: translateY(-1px);
  }
`;

// Action Menu Styled Components
const ActionMenuContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const ActionMenuButton = styled.button`
  background: #6b7280;
  color: white;
  border: none;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #4b5563;
    transform: translateY(-1px);
  }
`;

const ActionMenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 1000;
  min-width: 140px;
  margin-top: 4px;
`;

const ActionMenuItem = styled.button`
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  text-align: left;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #f3f4f6;
  }
  
  &:first-child {
    border-radius: 6px 6px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 6px 6px;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
`;

// Payment Expansion Panel Styled Components
const PaymentExpansionPanel = styled.div`
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin: 8px 0;
  overflow: hidden;
`;

const PaymentExpansionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f1f5f9;
  border-bottom: 1px solid #e5e7eb;
`;

const PaymentExpansionTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const PaymentCount = styled.span`
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
`;

const PaymentTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  
  th {
    background: #f9fafb;
    padding: 8px 12px;
    text-align: left;
    font-weight: 600;
    color: #374151;
    border-bottom: 1px solid #e5e7eb;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  td {
    padding: 8px 12px;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;
  }
  
  tr:hover {
    background: #f9fafb;
  }
  
  tr:last-child td {
    border-bottom: none;
  }
`;

const PaymentIdCell = styled.span`
  font-family: monospace;
  font-weight: 600;
  color: #f97316;
  font-size: 12px;
`;

const PaymentAmountCell = styled.span`
  font-weight: 600;
  color: #059669;
  font-size: 12px;
`;

const PaymentDateCell = styled.span`
  color: #374151;
  font-size: 12px;
`;

const PaymentStatusCell = styled.span<{ type: string }>`
  font-size: 11px;
  padding: 3px 6px;
  border-radius: 3px;
  font-weight: 500;
  
  ${props => {
    switch (props.type) {
      case 'active':
        return 'color: #059669; background: #d1fae5;';
      case 'voided':
        return 'color: #dc2626; background: #fee2e2;';
      case 'refund':
        return 'color: #ea580c; background: #fed7aa;';
      default:
        return 'color: #6b7280; background: #f3f4f6;';
    }
  }}
`;

const NoPaymentsMessage = styled.div`
  text-align: center;
  padding: 24px 16px;
  color: #6b7280;
  font-size: 13px;
  font-style: italic;
  background: #f9fafb;
`;

// Canonical Total Cell Styled Components
const TotalCell = styled.td`
  padding: 8px 12px;
  text-align: right;
  vertical-align: middle;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

const TotalAmount = styled.span`
  font-weight: 600;
  color: #059669;
  font-family: monospace;
  font-size: 14px;
`;

const LoadingText = styled.span`
  color: #6b7280;
  font-style: italic;
  font-size: 12px;
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #dc2626;
  font-size: 12px;
`;

const ErrorText = styled.span`
  color: #dc2626;
  font-size: 12px;
`;

const NoDataText = styled.span`
  color: #9ca3af;
  font-size: 12px;
  font-style: italic;
`;





// Debug Modal Styled Components
const DebugModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const DebugModalContent = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 90vw;
  max-height: 90vh;
  width: 800px;
  display: flex;
  flex-direction: column;
`;

const DebugModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const DebugModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
`;

const DebugModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const DebugDataContainer = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  overflow-x: auto;
  
  pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const DebugModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
`;

// Payments Cell Styled Components
const PaymentsCellContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11px;
  line-height: 1.2;
`;

const PaymentRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
`;

const PaymentLabel = styled.span`
  color: #6b7280;
  font-weight: 500;
  min-width: 40px;
`;

const PaymentAmount = styled.span`
  color: #059669;
  font-weight: 600;
  font-family: monospace;
  font-size: 10px;
`;

const PaymentCountBadge = styled.span`
  color: #059669;
  font-weight: 400;
  font-family: monospace;
  font-size: 10px;
`;

const BalanceAmount = styled.span<{ balance: number }>`
  font-weight: 600;
  font-family: monospace;
  font-size: 10px;
  color: ${props => props.balance > 0 ? '#dc2626' : props.balance < 0 ? '#ea580c' : '#059669'};
`;

// Invoice Status Badge Styled Component
const StatusBadge = styled.span<{ type: string }>`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 8px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.25px;
  white-space: nowrap;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  
  ${props => {
    switch (props.type) {
      case 'voided':
        return 'color: #dc2626; background: #fee2e2; border: 1px solid #fecaca;';
      case 'paid':
        return 'color: #059669; background: #d1fae5; border: 1px solid #a7f3d0;';
      case 'sent':
        return 'color: #2563eb; background: #dbeafe; border: 1px solid #bfdbfe;';
      case 'draft':
        return 'color: #6b7280; background: #f3f4f6; border: 1px solid #d1d5db;';
      case 'overdue':
        return 'color: #ea580c; background: #fed7aa; border: 1px solid #fdba74;';
      case 'unknown':
        return 'color: #9ca3af; background: #f9fafb; border: 1px solid #e5e7eb;';
      default:
        return 'color: #374151; background: #f3f4f6; border: 1px solid #d1d5db;';
    }
  }}
`;

const StatusBadgeCustom = styled.span`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 8px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.25px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
`;

export default InvoiceDetailCard; 